import React from "react";
import { map, mount, redirect, route, withView } from "navi";
import SignIn from "./signin";
import Home from "./home";
import MachineForm from "./machine/machineform";
import Configs from "./Configuration/configs";
import { ConfigForm } from "./Configuration/configform";
import { CreateConfig } from "./Configuration/configCreate";
import { Templates } from "./template/templates";
import TemplateForm from "./template/templateForm";
import { Layout } from "./layout/layout";

import templateService from "../services/api/templateService";
import TemplateSelector from "./template/TemplateSelector";
import machineService from "../services/api/machineService";
import configService from "../services/api/configService";
import ErrorBoundary from "./error";

/**
 *  Returns a route that's only accessible by authenticated users
 * @param {navi router matcher} matcher
 */
function withAuthentication(matcher) {
  return map((request, context) => {
    return context.isAuthenticated ? matcher : redirect("/login");
  });
}
/**
 *  returns the jsx child wrapped in the layout component (which displays the header)
 * @param {JSX} child the child to render
 */
function withHeader(child) {
  return <Layout>{child}</Layout>;
}

function handleError(err) {
  if (!err.response)
    return {
      view: <ErrorBoundary />
    };
  switch (err.response.status) {
    case 401: {
      localStorage.removeItem("jwtToken");
      return {
        view: <SignIn />
      };
    }
  }
}

export const routes = mount({
  "/": withAuthentication(
    route(async (request, context) => {
      const [res, err] = await machineService.getMachines();
      if (err) {
        return handleError(err);
      }
      const machines = res.data.sort((a, b) => a.machineId > b.machineId);
      return {
        view: withHeader(<Home />),
        data: { machines }
      };
    })
  ),
  "/login": map(async (request, context) =>
    context.isAuthenticated ? redirect("/") : withView(req => <SignIn />)
  ),
  "/machines/create": withAuthentication(
    route({
      view: withHeader(<MachineForm />)
    })
  ),
  "/machines/:machineid": withAuthentication(
    route(async (request, context) => {
      const [res, err] = await machineService.getMachine(
        request.params.machineid
      );
      if (err) {
        handleError(err);
      }
      const { data } = res;

      return {
        view: withHeader(<MachineForm />),
        data
      };
    })
  ),
  "/machines/:machineid/configs/:configid": withAuthentication(
    route(async (request, context) => {
      const { machineid, configid } = request.params;
      const [res, err] = await configService.getConfig(machineid, configid);
      if (err) {
        handleError(err);
      }
      const config = res.data;

      return {
        view: withHeader(<ConfigForm />),
        data: { isEditing: true, config }
      };
    })
  ),

  "/machines/:machineid/configs": withAuthentication(
    route(async (request, context) => {
      const [res, err] = await configService.getConfigs(
        request.params.machineid
      );
      if (err) {
        return handleError(err);
      }
      const configs = res.data;
      return {
        view: withHeader(<Configs />),
        data: {
          configs,
          machineid: request.params.machineid
        }
      };
    })
  ),
  "/templates": withAuthentication(
    route(async req => {
      const { page, ...rest } = req.params;
      const [res, err] = await templateService.getTemplates(page || 1, rest);
      if (err) {
        handleError(err);
      }
      return {
        view: withHeader(<Templates />),
        data: res.data
      };
    })
  ),
  "/templates/:templateid": withAuthentication(
    route(async req => {
      const templateId = req.params.templateid;
      const [res, err] = await templateService.getTemplate(templateId);
      if (err) {
        return handleError(err);
      }
      return {
        view: withHeader(<TemplateForm />),
        data: {
          isEditing: true,
          config: res.data
        }
      };
    })
  ),
  "/templates/create": withAuthentication(
    route({
      view: withHeader(<TemplateForm />)
    })
  ),
  "/machines/:machineid/configs/create": withAuthentication(
    route(async (request, context) => {
      // If true then we're  creating from scratch, false from template, if undefined then show the choice menu
      const isNew = request.params.new;
      if (isNew === undefined)
        return {
          view: withHeader(<CreateConfig />),
          data: { machineId: request.params.machineid }
        };
      if (isNew === "true") {
        return {
          view: withHeader(<ConfigForm />),
          data: { machineId: request.params.machineid }
        };
      }
      const { page, machineid, new: _, ...rest } = request.params;
      const [res, err] = await templateService.getTemplates(page || 1, rest);
      if (err) {
        return handleError(err);
      }
      return {
        view: withHeader(<TemplateSelector />),
        data: {
          templates: res.data,
          machineId: request.params.machineid
        }
      };
    })
  )
});



// WEBPACK FOOTER //
// ./src/views/routes.jsx